/**
 * DescriptionCard - Card "Descrição" da sidebar T1.
 *
 * Substitui a antiga aba "Detalhes" (`TicketDetailsTab`, agora sem uso) pros
 * 3 campos descritivos do ticket — descrição, notas internas e resumo da
 * resolução. Fix do bug lossy do `EditableText` legado: aquele componente
 * fazia `stripHtml()` tanto pra EXIBIR quanto pra abrir o modo de edição —
 * ou seja, editar um campo com HTML (negrito, links, listas — o
 * `TicketFormDrawer` já grava a descrição como HTML do `RichTextEditor`)
 * reabria com o texto já sem formatação e a próxima gravação persistia o
 * texto puro, destruindo a formatação original silenciosamente. Aqui o modo
 * de edição sempre recebe o HTML original intacto (`value` vai direto pro
 * `RichTextEditor`, nunca por `stripHtml`), e o modo de leitura usa `SafeHtml`
 * (mesma sanitização client-side da Timeline V2) em vez de extrair texto.
 */
import React, { useState } from 'react';
import { RichTextEditor } from '@/shared/components/ui/RichTextEditor';
import { SidebarCard } from './SidebarCard';
import { SafeHtml } from '../timeline/SafeHtml';
import type { PopulatedTicket, UpdateTicketRequest } from '../../types';
import { Icon } from '@/shared/icons';

export interface DescriptionCardProps {
  ticket: PopulatedTicket;
  onUpdate: (data: Partial<UpdateTicketRequest>) => void;
  loading?: boolean;
}

const RICHTEXT_FEATURES = {
  bold: true,
  italic: true,
  underline: true,
  bulletList: true,
  orderedList: true,
  link: true,
  blockquote: true,
  heading: false,
  image: false,
  attachment: false,
  code: false,
  codeBlock: false,
  textAlign: false,
};

/** Espelha o check de "vazio mas com imagem" do `TicketEditor` — HTML "vazio"
 * do TipTap normalmente é `<p></p>`; sem essa checagem uma imagem sem texto
 * ao redor seria descartada como se não houvesse conteúdo. */
function isHtmlContentEmpty(html: string): boolean {
  if (/<img[\s/>]/i.test(html)) return false;
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim();
  return text.length === 0;
}

interface EditableRichFieldProps {
  label: string;
  value: string;
  placeholder: string;
  onSave: (html: string) => void;
  loading?: boolean;
  testId: string;
}

const EditableRichField: React.FC<EditableRichFieldProps> = ({ label, value, placeholder, onSave, loading, testId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const startEditing = () => {
    setDraft(value);
    setEditing(true);
  };

  const handleSave = () => {
    if (draft !== value) onSave(draft);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const isEmpty = !value || isHtmlContentEmpty(value);

  return (
    <div className="py-2" style={{ borderBottom: '1px solid var(--theme-card-border, #f1f5f9)' }}>
      <div className="flex items-center justify-between mb-1">
        {/* micro-label UPPERCASE 11px bold do mockup (DESCRIÇÃO / NOTAS INTERNAS / …) */}
        <span className="text-[11px] font-bold uppercase" style={{ color: 'var(--theme-muted-text)', letterSpacing: '.04em' }}>{label}</span>
        {!editing && (
          <button
            type="button"
            onClick={startEditing}
            className="p-1 rounded transition-colors"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid={`${testId}-edit`}
            aria-label={`Editar ${label}`}
          >
            <Icon name="edit" className="w-3 h-3" />
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            placeholder={placeholder}
            minHeight={100}
            showToolbar
            disabled={loading}
            features={RICHTEXT_FEATURES}
            style={{ border: '1px solid var(--theme-card-border)', borderRadius: '0.5rem', overflow: 'hidden' }}
          />
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-white transition-colors"
              style={{ backgroundColor: 'var(--theme-primary)' }}
              data-testid={`${testId}-save`}
            >
              <Icon name="check" className="w-3 h-3" />
              Salvar
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              data-testid={`${testId}-cancel`}
            >
              <Icon name="x" className="w-3 h-3" />
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        /* div[role=button] em vez de <button>: o SafeHtml pode conter ul/a/
         * blockquote (block-level/interativos) — DOM nesting inválido dentro
         * de <button>. Teclado coberto via Enter/Espaço no onKeyDown. */
        <div
          role="button"
          tabIndex={0}
          onClick={startEditing}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startEditing();
            }
          }}
          className="w-full text-left rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer"
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          aria-label={`Editar ${label}`}
          data-testid={testId}
        >
          {isEmpty ? (
            <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{placeholder}</span>
          ) : (
            <SafeHtml html={value} className="text-xs prose-sm" data-testid={`${testId}-content`} />
          )}
        </div>
      )}
    </div>
  );
};

export const DescriptionCard: React.FC<DescriptionCardProps> = ({ ticket, onUpdate, loading = false }) => {
  return (
    <SidebarCard
      icon="file-text"
      title="Descrição"
      collapsible
      data-testid="ticket-detail-description-card"
    >
      <EditableRichField
        label="Descrição"
        value={ticket.description || ''}
        placeholder="Nenhuma descrição"
        onSave={(description) => onUpdate({ description })}
        loading={loading}
        testId="ticket-detail-description"
      />
      <EditableRichField
        label="Notas internas"
        value={ticket.internalNotes || ''}
        placeholder="Nenhuma nota interna"
        onSave={(internalNotes) => onUpdate({ internalNotes })}
        loading={loading}
        testId="ticket-detail-internal-notes"
      />
      <EditableRichField
        label="Resumo da resolução"
        value={ticket.resolutionSummary || ''}
        placeholder="Nenhum resumo de resolução"
        onSave={(resolutionSummary) => onUpdate({ resolutionSummary })}
        loading={loading}
        testId="ticket-detail-resolution-summary"
      />
    </SidebarCard>
  );
};

DescriptionCard.displayName = 'DescriptionCard';
